<template>
	<div class="merchants-chart-container">
		<v-chart
			v-if="chartOption"
			ref="chart"
			:option="chartOption"
			:style="{ height: '250px', width: '100%', cursor: 'pointer' }"
			autoresize
			@click="handleClick"
		/>
		<div v-else class="no-data">No merchant data available</div>
	</div>
</template>

<script>
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { BarChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import VChart from 'vue-echarts';

use([CanvasRenderer, BarChart, GridComponent, TooltipComponent]);

export default {
	name: 'TopMerchantsChart',
	components: { VChart },
	props: {
		merchants: { type: Array, required: true },
		formatter: { type: Object, required: true },
		colourMode: { type: String, default: 'light' }
	},
	emits: ['merchant-click'],
	methods: {
		handleClick(params) {
			if (params.name) {
				this.$emit('merchant-click', params.name);
			}
		}
	},
	computed: {
		chartOption() {
			if (!this.merchants || this.merchants.length === 0) return null;

			const textColor = this.colourMode === 'dark' ? '#e0e0e0' : '#333';
			const gridColor = this.colourMode === 'dark' ? '#333' : '#e0e0e0';

			const names = this.merchants.map(m => m.name).reverse();
			const values = this.merchants.map(m => m.value).reverse();

			return {
				tooltip: {
					trigger: 'axis',
					axisPointer: { type: 'shadow' },
					formatter: (params) => {
						return `${params[0].name}: ${this.formatter.format(params[0].value)}`;
					}
				},
				grid: {
					left: '3%',
					right: '10%',
					bottom: '3%',
					top: '3%',
					containLabel: true
				},
				xAxis: {
					type: 'value',
					axisLine: { lineStyle: { color: gridColor } },
					splitLine: { lineStyle: { color: gridColor } },
					axisLabel: {
						color: textColor,
						formatter: (value) => {
							if (value >= 1000) return (value / 1000).toFixed(0) + 'k';
							return value;
						}
					}
				},
				yAxis: {
					type: 'category',
					data: names,
					axisLine: { lineStyle: { color: gridColor } },
					axisLabel: {
						color: textColor,
						width: 100,
						overflow: 'truncate'
					}
				},
				series: [
					{
						type: 'bar',
						data: values,
						itemStyle: {
							color: {
								type: 'linear',
								x: 0, y: 0, x2: 1, y2: 0,
								colorStops: [
									{ offset: 0, color: '#9EA2D0' },
									{ offset: 1, color: '#413655' }
								]
							},
							borderRadius: [0, 4, 4, 0]
						},
						barMaxWidth: 30,
						label: {
							show: true,
							position: 'right',
							formatter: (params) => this.formatter.format(params.value),
							color: textColor,
							fontSize: 11
						}
					}
				]
			};
		}
	}
};
</script>

<style scoped>
.merchants-chart-container {
	width: 100%;
}

.no-data {
	height: 250px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: var(--light-text-secondary);
}
</style>
